/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { SysqueCheckBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-check-box-vm';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { SysqueRadioButtonVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-radio-button-vm';
import {
  ANNOTATION_SETTINGS_FACADE_TOKEN,
  IAnnotationSettingsFacade
} from '../../../../../../../../../../data-access/src/lib/domain/facades-models/annotation-settings-facade-interface';
import { GlobalConstants } from '../../../../../../../constants/global-constants';
import { Annotation, DisplayUnit } from '../../../../../../../models/annotation-settings';
import { Subscription } from 'rxjs';

@Component({
  selector: 'cable-tray-sysque-size-tag',
  templateUrl: './cable-tray-size-tag.component.html',
  styleUrls: ['./cable-tray-size-tag.component.scss'],
  standalone: false
})
export class CableTraySizeTagComponent implements OnInit, OnDestroy {
  sizeDataFromXml: Annotation;
  cableTraySizeDataSelection: Annotation;
  getUnitSubscription: Subscription;
  selectedPathSubscription: Subscription;
  selectedFittingsPathSubscription: Subscription;

  //Placement initialization
  placementLabel = GlobalConstants.PLACEMENT_LABEL;
  placementPlaceholder = GlobalConstants.DEFAULT;
  placementSelectedValue = '';
  placementItems: string[] = [GlobalConstants.ABOVE, GlobalConstants.CENTER, GlobalConstants.BELOW];

  //Offset initialization
  showOffsetSpinButtons = true;
  offsetItems: SysqueNumberBoxVM[] = [];

  //Occurrence initialization
  occurrenceLabel = GlobalConstants.OCCURRENCE_LABEL;
  occurrenceItems: SysqueRadioButtonVM[] = [];

  //Properties initialization
  propertiesLabel = GlobalConstants.PROPERTIES_LABEL;
  propertiesSelectedValue = '';
  propertiesItems: SysqueCheckBoxVM[] = [];

  //Ignore straights under initialization
  ignoreStraightsLabel = GlobalConstants.IGNORE_STRAIGHTS_LABEL;
  ignoreStraightsSpinButtons = true;
  ignoreStraightsItems: SysqueNumberBoxVM[] = [];

  //Location initialization
  locationLabel: string = GlobalConstants.SIZE_LOCATION_LABEL;
  locationPlaceholder = '';
  locationPath = '';

  //Fitting Location initialization
  fittingLocationLabel = GlobalConstants.SIZE_FITTING_LOCATION_LABEL;
  fittingLocationPlaceholder = '';
  fittingLocationPath = '';

  constructor(@Inject(ANNOTATION_SETTINGS_FACADE_TOKEN) private annotationSettingsFacade: IAnnotationSettingsFacade) {}

  public ngOnInit(): void {
    //retrieve data from xml file
    this.sizeDataFromXml = this.annotationSettingsFacade.getTagDataFromXmlByTabNameAndAnnotationName(
      GlobalConstants.CABLE_TRAY_TAB_NAME,
      GlobalConstants.SIZE_ANNOTATION_NAME
    );

    //retrieve selection data from local storage
    this.cableTraySizeDataSelection = this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME);

    //if there is a selection data from local storage this means that it wasn't the first load for the annotation settings dialog
    //and the user can have a previous selection and we don't want to lose this selection
    //and load it
    if (this.cableTraySizeDataSelection !== null) {
      this.loadData(this.cableTraySizeDataSelection);
    } else {
      //else load data from xml file
      this.loadData(this.sizeDataFromXml);

      //there wasn't a selection yet so we need to save the default selection to the local storage in order to set each property when the user start to make a selection
      this.annotationSettingsFacade.setDefaultSelectionToStorageByTabNameAndAnnotationName(this.sizeDataFromXml, GlobalConstants.CABLE_TRAY_TAB_NAME, GlobalConstants.SIZE_ANNOTATION_NAME,
        GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME);
    }
  }

  public loadData(data: Annotation): void {
    //Placement value initialization from xml file
    this.placementSelectedValue = data.location || this.placementItems[0];

    //Offset items initialization from xml file
    let unit = DisplayUnit.Metric;
    this.getUnitSubscription = this.annotationSettingsFacade.getRevitInformationFromStore().subscribe(data => {
      unit = data.revitDisplayUnit;
    });

    this.offsetItems = [
      { label: GlobalConstants.X_OFFSET, value: parseInt(data.xOffset), isMetric: unit === DisplayUnit.Metric },
      { label: GlobalConstants.Y_OFFSET, value: parseInt(data.yOffset), isMetric: unit === DisplayUnit.Metric }
    ];

    //Occurrence items initialization from xml file
    this.occurrenceItems = [
      {
        label: GlobalConstants.AT_SIZE_CHANGE,
        isChecked: this.annotationSettingsFacade.getOccurrencePropertyByName(GlobalConstants.SIZE_CHANGE, data)
      },
      {
        label: GlobalConstants.UPON_SIZE_OR_DIRECTION_CHANGE,
        isChecked: this.annotationSettingsFacade.getOccurrencePropertyByName(GlobalConstants.DIRECTION, data)
      }
    ];

    //Properties items initialization from xml file
    this.propertiesItems = [
      {
        label: GlobalConstants.SHOW_LEADER,
        isChecked: this.annotationSettingsFacade.getPropertiesPropertyByName(GlobalConstants.SHOW_LEADER, data)
      },
      {
        label: GlobalConstants.HORIZONTAL_TEXT,
        isChecked: this.annotationSettingsFacade.getPropertiesPropertyByName(GlobalConstants.HORIZONTAL_TEXT, data)
      },
      {
        label: GlobalConstants.SHOW_BORDER,
        isChecked: this.annotationSettingsFacade.getPropertiesPropertyByName(GlobalConstants.SHOW_BORDER, data)
      }
    ];

    //Ignore straights under items initialization from xml file
    this.ignoreStraightsItems = [
      { label: '', value: parseInt(data.ignoreLength), isMetric: unit === DisplayUnit.Metric, minValue: 0 }
    ];

    //Location initialization from xml file
    this.locationPath = data.tagPath;

    //Fitting Location initialization from xml file
    this.fittingLocationPath = data.fittingTagPath;

    //load image and labels
    const tagsState = this.annotationSettingsFacade.getTagsStateFromStorageByTabName(
      GlobalConstants.CABLE_TRAY_TAB_NAME
    );
    this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CABLE_TRAY_TAB_NAME, tagsState, [data]);
  }

  public onPlacementValueChanged(item: string): void {
    this.annotationSettingsFacade.setPlacementValueToStorage(GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME, item);
    //load image and labels
    this.loadImage();
  }

  public onOffsetValueChanged(item: { label: string, value: number }): void {
    this.annotationSettingsFacade.setOffsetValueToStorage(GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME, item.label, item.value);
    //load image and labels
    this.loadImage();
  }

  public onOccurrenceValueChanged(label: string): void {
    this.propertiesSelectedValue = label;
    this.annotationSettingsFacade.setOccurrenceValueToStorage(
      GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME,
      label
    );
    //load image and labels
    this.loadImage();
  }

  public onPropertiesValueChanged(item: SysqueCheckBoxVM): void {
    this.annotationSettingsFacade.setPropertiesValueToStorage(
      GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME,
      item.label,
      item.isChecked
    );
  }

  public onIgnoreStraightValueChanged(item: { label: string; value: number }): void {
    this.annotationSettingsFacade.setIgnoreStraightsValueToStorage(
      GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME,
      item.value
    );
  }

  public onLocationValueChanged(item: string): void {
    this.annotationSettingsFacade.setTagPathValueToStorage(GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME, item);
  }

  public onFittingsLocationValueChanged(item: string): void {
    this.annotationSettingsFacade.setFittingTagPathValueToStorage(GlobalConstants.CABLE_TRAY_ELEVATION_DATA_SELECTION_NAME, item);
  }

  public getTagLocationPath(): void {
    this.selectedPathSubscription = this.annotationSettingsFacade.getTagLocationPath().subscribe(data => {
      this.locationPath = data;
    });
  }

  public getFittingsTagLocationPath(): void {
    this.selectedFittingsPathSubscription = this.annotationSettingsFacade.getFittingsTagLocationPath().subscribe((data) => {
      this.fittingLocationPath = data;
    });
  }

  public ngOnDestroy(): void {
    if (this.getUnitSubscription) {
      this.getUnitSubscription.unsubscribe();
    }
    if (this.selectedPathSubscription) {
      this.selectedPathSubscription.unsubscribe();
    }
    if (this.selectedFittingsPathSubscription) {
      this.selectedFittingsPathSubscription.unsubscribe();
    }
  }

  public loadImage(): void {
    const tagsState = this.annotationSettingsFacade.getTagsStateFromStorageByTabName(
      GlobalConstants.CABLE_TRAY_TAB_NAME
    );
    const selectionData = this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
      GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME
    );
    this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CABLE_TRAY_TAB_NAME, tagsState, [
      selectionData
    ]);
  }
}
